/* eslint-disable @next/next/no-img-element */

/* eslint-disable custom/no-hardcoded-colors */
import { withOnClickTracking } from 'auto-tracking';
import { ArrowUpRightFromSquare, Button, styled, theme } from 'design-system';
import { useTranslation } from 'next-i18next';
import React from 'react';

import { Journal } from '../../../resources/journal';
import { mutateString } from '../../../utils/replace-language';

const Container = styled('div', {
  textAlign: 'center',
  padding: '40px 0px',
  paddingTop: '0',
});
const Container2 = styled('div', {
  display: 'flex',
  alignItems: 'left',
  justifyContent: 'left',
});

const Title = styled('div', {
  fontSize: '20px',
  fontWeight: '700',
  color: '#0054F1',
  lineHeight: '30px',
  letterSpacing: '-4%',
  marginBottom: '10px',
  textTransform: 'uppercase',
});
const TextContainer2 = styled('div', {
  display: 'flex',
  alignItems: 'center',
  fontSize: 16,
  fontWeight: 600,
  color: theme.colors.blue12,
  gap: '7px',
  '&:hover': {
    color: theme.colors.blue9,
  },
});
const Description = styled('p', {
  fontFamily: 'IBM Plex Serif',
  fontSize: '24px',
  fontWeight: '600',
  color: '#051E57',
  lineHeight: '44.6px',
  letterSpacing: '0%',
  textAlign: 'center',
  marginBottom: '60px',
});

const ViewSample = withOnClickTracking(
  styled('div', {
    padding: '0',
    marginTop: '6px',
  }),
);

// const UploadButtonContainer = styled('div', {
//   marginTop: '16px',
//   display: 'flex',
//   justifyContent: 'center',
// });

const StepsContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  maxWidth: '1200px',
  margin: '0 auto',
});

const Step = styled('div', {
  flex: '1',
  minWidth: '250px',
  textAlign: 'left',
  padding: '20px',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'left',
});

const Icon = styled('div', {
  width: '60px',
  height: '60px',
  //borderRadius: '50%',
  //background: theme.colors.blue12,
  display: 'flex',
  justifyContent: 'left',
  alignItems: 'left',
  marginBottom: '10px',
  position: 'relative',
  zIndex: 1,
});

const Line = styled('div', {
  position: 'absolute',
  top: '50px',
  zIndex: 0,
  width: '430px',
  height: '1px',
  background: '#E4ECF6',
  '@media (max-width: 768px)': {
    display: 'none',
  },
  variants: {
    type: {
      variant1: {
        height: '1px',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          top: '15px',
          left: '10%',
          width: '250px',
          height: '1px',
          background: '#E4ECF6',
        },
        '&::before': {
          top: '-15px',
        },
      },
      variant2: {
        height: '1px',
        background:
          'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(228, 236, 246, 0.8) 68%, rgba(228, 236, 246, 0) 100%)',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          top: '15px',
          left: '0',
          width: '180px',
          height: '1px',
          background:
            'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(228, 236, 246, 0.8) 68%, rgba(228, 236, 246, 0) 100%)',
        },
        '&::before': {
          top: '-15px',
        },
      },
      variant3: {
        height: '1px',
        border: '1px solid',
        borderImageSource:
          'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(228, 236, 246, 0.8) 68%, rgba(228, 236, 246, 0) 100%)',
      },
      variant4: {
        height: '1px',
        background: '#E4ECF6',
      },
    },
  },
});

const StepTitle = styled('div', {
  fontFamily: 'IBM Plex Sans',
  fontSize: '16px',
  fontWeight: '600',
  color: theme.colors.blue12,
  lineHeight: '24px',
  letterSpacing: '0%',
  marginBottom: '5px',
  textAlign: 'left',
});

const StepDescription = styled('p', {
  fontFamily: 'IBM Plex Sans',
  fontSize: '16px',
  fontWeight: '400',
  color: theme.colors.blue12,
  lineHeight: '22px',
  letterSpacing: '0%',
  textAlign: 'left',
  marginTop: '10px',
});

const Step1 = ({
  journal,
  useGJDContent,
}: {
  journal: Journal;
  useGJDContent?: boolean;
}) => {
  const t = useTranslation('homepage').t;
  return (
    <Step data-test-id="step1">
      <Icon
        aria-label={
          useGJDContent
            ? t('gjd.paperpal_upload_document', 'Upload your document')
            : t('section3_v2.upload_your_document', 'Upload your document')
        }
      >
        <img
          src="/1.svg"
          alt={
            useGJDContent
              ? t('gjd.paperpal_upload_document', 'Upload your document')
              : t('section3_v2.upload_your_document', 'Upload your document')
          }
        />
      </Icon>
      <StepTitle data-test-id="step1-title">
        {useGJDContent
          ? t('gjd.paperpal_upload_document', 'Upload your document')
          : t('section3_v2.upload_your_document', 'Upload your document')}
      </StepTitle>
      <StepDescription data-test-id="step1-description">
        {useGJDContent
          ? t(
              'gjd.paperpal_gets_to_work',
              'Paperpal gets to work immediately, using AI to evaluate your manuscript against the checks hand-picked by journal editors.',
            )
          : journal.publisher.slug === 'paperpal' ||
            journal.publisher.slug === 'gjd-journal-finder' ||
            journal.publisher.slug === 'editage-cn' ||
            journal.publisher.slug === 'editage-us'
          ? t(
              'section3_v2.we_will_review_your_manuscript_paperpal',
              'Preflight gets to work immediately, using AI to evaluate based on journal requirements.',
            )
          : t(
              'section3_v2.we_will_review_your_manuscript',
              'Preflight gets to work immediately, using AI to evaluate based on journal requirements.',
            )}
      </StepDescription>
      <Line type={'variant1'} />
    </Step>
  );
};

const Step2 = ({
  langCheckCount,
  techCheckCount,
  journal,
}: {
  langCheckCount: number;
  techCheckCount: number;
  journal: Journal;
}) => {
  const t = useTranslation('homepage').t;
  return (
    <Step data-test-id="step2">
      <Icon
        aria-label={t(
          'section3_v2.get_submission_readiness_report',
          'Get submission readiness report',
        )}
      >
        <img
          src="/2.svg"
          alt={t(
            'section3_v2.get_submission_readiness_report',
            'Get submission readiness report',
          )}
        />
      </Icon>
      <StepTitle data-test-id="step2-title">
        {t(
          'section3_v2.get_submission_readiness_report',
          'Get submission readiness report',
        )}
      </StepTitle>
      <StepDescription data-test-id="step2-description">
        {journal.publisher.slug === 'paperpal' ||
        journal.publisher.slug === 'gjd-journal-finder' ||
        journal.publisher.slug === 'editage-cn' ||
        journal.publisher.slug === 'editage-us'
          ? t(
              'section3_v2.view_your_document_s_analysis_paperpal',
              'Preflight gets to work immediately, using AI to evaluate based on journal requirements.',
            )
          : mutateString(
              t(
                'section3_v2.view_your_document_s_analysis',
                'You’ll receive a detailed report showing how your manuscript stacks up to submission standards - for FREE.',
              ),
              {
                '{X}': `${langCheckCount}`,
                '{Y}': `${techCheckCount}`,
              },
            )}
      </StepDescription>
      <Line type={'variant2'} />
    </Step>
  );
};

const Step3 = ({
  priceText,
  useGJDContent,
  isPeerPilotEnabled = true,
}: {
  priceText: string;
  useGJDContent?: boolean;
  isPeerPilotEnabled?: boolean;
}) => {
  const t = useTranslation('homepage').t;
  return (
    <Step data-test-id="step3">
      <Icon
        aria-label={
          isPeerPilotEnabled
            ? t(
                'section3_v2.download_the_edited_file_with_peerpilot',
                'Access PeerPilot & Edited File',
              )
            : t(
                'section3_v2.download_the_edited_file_without_peerpilot',
                'Download the edited file',
              )
        }
      >
        <img
          src="/3.svg"
          alt={
            isPeerPilotEnabled
              ? t(
                  'section3_v2.download_the_edited_file_with_peerpilot',
                  'Access PeerPilot & Edited File',
                )
              : t(
                  'section3_v2.download_the_edited_file_without_peerpilot',
                  'Download the edited file',
                )
          }
        />
      </Icon>
      <StepTitle data-test-id="step3-title">
        {isPeerPilotEnabled
          ? t(
              'section3_v2.download_the_edited_file_with_peerpilot',
              'Access PeerPilot & Edited File',
            )
          : t(
              'section3_v2.download_the_edited_file_without_peerpilot',
              'Download the edited file',
            )}
      </StepTitle>
      <StepDescription data-test-id="step3-description">
        {useGJDContent
          ? t(
              'gjd.paperpal_provides_tracked_version',
              'Paperpal provides a tracked version highlighting issues and fixes. Premium version starts at $2 per check.',
            )
          : mutateString(
              isPeerPilotEnabled
                ? t(
                    'section3_v2.preflight_will_resolve_all_identified_issues_with_peerpilot',
                    'Upgrade to access the full PeerPilot report and download the tracked AI-Edited File ({priceText} fee)',
                  )
                : t(
                    'section3_v2.preflight_will_resolve_all_identified_issues_without_peerpilot',
                    'Preflight provides a tracked version showing all the changes your manuscript needs – for a one-time fee of {priceText}',
                  ),
              {
                '{priceText}': `${priceText}`,
              },
            )}
      </StepDescription>
      <Container2>
        <ViewSample>
          <Button
            data-test-id="view-sample"
            color="tertiaryReversed"
            onClick={() =>
              window.open(
                '/Sample_Electronics and Electrical Engg SES (Paperpal Edit).pdf',
                '_blank',
              )
            }
            css={{
              textDecoration: 'none',
              padding: '0',
              background: 'none',
            }}
          >
            <TextContainer2 data-test-id="view-the-sample">
              {t('section3_v2.view_sample', 'View sample')}
              <ArrowUpRightFromSquare width="16px" />
            </TextContainer2>
          </Button>
        </ViewSample>
      </Container2>
      <Line type={'variant4'} />
    </Step>
  );
};

const Step4 = ({
  isPeerPilotEnabled = true,
}: {
  isPeerPilotEnabled?: boolean;
}) => {
  const t = useTranslation('homepage').t;
  return (
    <Step data-test-id="step4">
      <Icon
        aria-label={
          isPeerPilotEnabled
            ? t(
                'section3_v2.submit_with_confidence_peerpilot',
                'Submit with Assurance',
              )
            : t(
                'section3_v2.submit_with_confidence_no_peerpilot',
                'Submit with Confidence',
              )
        }
      >
        <img
          src="/4.svg"
          alt={
            isPeerPilotEnabled
              ? t(
                  'section3_v2.submit_with_confidence_peerpilot',
                  'Submit with Assurance',
                )
              : t(
                  'section3_v2.submit_with_confidence_no_peerpilot',
                  'Submit with Confidence',
                )
          }
        />
      </Icon>
      <StepTitle data-test-id="step4-title">
        {isPeerPilotEnabled
          ? t(
              'section3_v2.submit_with_confidence_peerpilot',
              'Submit with Assurance',
            )
          : t(
              'section3_v2.submit_with_confidence_no_peerpilot',
              'Submit with Confidence',
            )}
      </StepTitle>
      <StepDescription data-test-id="step4-description">
        {isPeerPilotEnabled
          ? t(
              'section3_v2.your_purchase_includes_30_days_peerpilot',
              'PeerPilot provides an initial content review for clarity. The AI-edited file then offers a 30-day recheck to help ensure optimal language quality for submission.',
            )
          : t(
              'section3_v2.your_purchase_includes_30_days_no_peerpilot',
              'With free rechecks for 30 days after the purchase, Preflight ensures nothing holds back your submission.',
            )}
      </StepDescription>
    </Step>
  );
};

export const TakePreflightSection = ({
  price,
  selectedPaymentCurrency,
  localCurrencyEnabled,
  langCheckCount,
  techCheckCount,
  journal,
  useGJDContent = false,
}: {
  price?: string | number;
  selectedPaymentCurrency?: string;
  localCurrencyEnabled?: boolean;
  langCheckCount: number;
  techCheckCount: number;
  journal: Journal;
  useGJDContent?: boolean;
}) => {
  const t = useTranslation('homepage').t;
  const priceText = `${price} ${
    selectedPaymentCurrency &&
    selectedPaymentCurrency === 'INR' &&
    localCurrencyEnabled
      ? ' (tax included)'
      : ''
  }`;

  return (
    <Container>
      <Title>
        {useGJDContent
          ? t('gjd.take_paperpal_to_submission', 'Take Paperpal to Submission')
          : t(
              'section3_v2.take_preflight_to_submission',
              'Take Preflight to Submission',
            )}
      </Title>
      <Description>
        {t(
          'section3_v2.submission_ready',
          'See how we can make you submission ready',
        )}
      </Description>
      <StepsContainer>
        <Step1 journal={journal} useGJDContent={useGJDContent} />
        <Step2
          langCheckCount={langCheckCount}
          techCheckCount={techCheckCount}
          journal={journal}
        />
        <Step3
          priceText={priceText}
          useGJDContent={useGJDContent}
          isPeerPilotEnabled={journal.peerPilotEnabled}
        />
        <Step4 isPeerPilotEnabled={journal.peerPilotEnabled} />
      </StepsContainer>
    </Container>
  );
};
